<template>
  <div>
    <div class="w-80">
      <NLoading v-if="isLoadingSymbol" class="text-brand-primary" size="10" />

      <div v-else class="flex flex-col p-3">
        <div class="flex justify-between">
          <div class="inline-flex items-center">
            <SymbolTradeStateIcon
              class="me-2"
              size="medium"
              :trading-state-code="symbol.instrumentTradingState"
            />

            <Retail :isin="getSymbolIsin(symbol)">
              <NText variant="h3" tag="h3" weight="bold">
                {{ symbol.instrumentName }}
              </NText>
            </Retail>
          </div>

          <div class="flex flex-col justify-around">
            <div class="inline-flex items-center">
              <NumberFormatter
                #="{ number }"
                :value="(symbol.referencePriceVariationPercentage ?? 0) / 100"
                maximum-fraction-digits="2"
                sign-display="always"
                number-style="percent"
              >
                <NText
                  weight="bold"
                  dir="ltr"
                  class="mx-1"
                  :class="{
                    'text-brand-primary':
                      (symbol.referencePriceVariationPercentage ?? 0) > 0,
                    'text-critical-primary':
                      (symbol.referencePriceVariationPercentage ?? 0) < 0,
                  }"
                >
                  ({{ number }})
                </NText>
              </NumberFormatter>

              <NumberFormatter #="{ number }" :value="symbol.lastTradePrice">
                <ChangePriceEffect :price="symbol.lastTradePrice ?? 0">
                  <NText :key="number" variant="h1" tag="h1" weight="bold">
                    {{ number }}
                  </NText>
                </ChangePriceEffect>
              </NumberFormatter>
            </div>
          </div>
        </div>

        <NText variant="caption1" class="text-neutral-secondary">
          {{ symbol.instrumentTitle }}
        </NText>
      </div>
    </div>

    <div class="mt-4 p-4 bg-neutral-secondary rounded-md">
      <div class="flex justify-between items-center">
        <NText variant="caption1" weight="medium">
          {{ t('orders.orderType') }}
        </NText>

        <NText variant="caption1" weight="medium" class="pr-2">
          {{ t('modules.order.SLTP') }}
        </NText>
      </div>
      <div class="flex justify-between items-center py-2">
        <NText variant="caption1" weight="medium">
          {{ t('orders.orderSide') }}
        </NText>

        <NText
          variant="caption1"
          weight="medium"
          class="pr-2 text-critical-primary"
        >
          {{ t('common.sell') }}
        </NText>
      </div>

      <div
        class="flex justify-between items-center pb-2 border-b border-neutral-tertiary"
      >
        <NText variant="caption1" weight="medium">
          {{ t('modules.order.order_state') }}
        </NText>

        <NText variant="caption1" weight="medium" class="pr-2">
          {{ order.orderState }}
        </NText>
      </div>

      <div class="flex justify-between items-center py-2">
        <NText variant="caption1" weight="medium">
          {{ t('modules.order.requestNumber') }}
        </NText>

        <NText variant="caption1" weight="medium">
          {{
            t('modules.order.percentOfAsset', {
              percent: order.percent,
            })
          }}
        </NText>
      </div>

      <div
        v-if="order.targetQuantity"
        class="flex justify-between items-center pb-2"
      >
        <NText variant="caption1" weight="medium">
          {{ t('misc.quantity') }}
          ({{ t('modules.order.executed') }})
        </NText>

        <div class="flex flex-row items-center">
          <NumberFormatter #="{ number }" :value="order.executedQuantity">
            <NText variant="caption1" weight="medium" class="pl-1">
              ({{ number }})
            </NText>
          </NumberFormatter>

          <NumberFormatter #="{ number }" :value="Number(order.targetQuantity)">
            <NText variant="caption1" weight="medium">{{ number }}</NText>
          </NumberFormatter>
        </div>
      </div>

      <div class="flex justify-between items-center pb-2">
        <NText variant="caption1" weight="medium">
          {{ t('modules.order.profitLimit') }}
        </NText>

        <div v-if="order.upperTrigger">
          <NumberFormatter #="{ number }" :value="order.upperTrigger">
            <NText variant="caption1" weight="medium">
              {{ number }}
            </NText>
          </NumberFormatter>

          <NText variant="overline" class="text-neutral-secondary pr-1">
            {{ t('misc.rial') }}
          </NText>
        </div>

        <div v-else>
          <NText variant="caption1" class="text-neutral-secondary"> - </NText>
        </div>
      </div>

      <div
        v-if="order.upperTrigger"
        class="flex justify-between items-center pb-2"
      >
        <NText variant="caption1" weight="medium">
          {{ t('modules.order.profitLimitOrderPrice') }}
        </NText>

        <NText variant="caption1" weight="medium">
          {{ t('modules.order.priceType.marketPrice.title') }}
        </NText>
      </div>

      <div class="flex justify-between items-center pb-2">
        <NText variant="caption1" weight="medium">
          {{ t('modules.order.limitOfLoss') }}
        </NText>

        <div v-if="order.lowerTrigger">
          <NumberFormatter #="{ number }" :value="order.lowerTrigger">
            <NText variant="caption1" weight="medium">
              {{ number }}
            </NText>
          </NumberFormatter>

          <NText variant="overline" class="text-neutral-secondary pr-1">
            {{ t('misc.rial') }}
          </NText>
        </div>

        <div v-else>
          <NText variant="caption1" class="text-neutral-secondary"> - </NText>
        </div>
      </div>

      <div
        v-if="order.lowerTrigger"
        class="flex justify-between items-center pb-2"
      >
        <NText variant="caption1" weight="medium">
          {{ t('modules.order.limitOfLossOrderPrice') }}
        </NText>

        <NText variant="caption1" weight="medium">
          {{ t('modules.order.priceType.marketPrice.title') }}
        </NText>
      </div>

      <template v-if="order.targetQuantity">
        <div
          v-if="algorithmAverageTransactionPrice(order, wage)"
          class="flex justify-between items-center pb-2"
        >
          <NText variant="caption1" weight="medium">
            {{ t('modules.order.AverageTransactionPrice') }}
          </NText>

          <div class="flex flex-row items-center">
            <NumberFormatter
              #="{ number }"
              :value="algorithmAverageTransactionPrice(order, wage)"
              round
            >
              <NText variant="caption1" weight="medium" class="pl-1">
                {{ number }}
              </NText>
            </NumberFormatter>

            <NText variant="overline" class="text-neutral-secondary">
              {{ t('misc.rial') }}
            </NText>
          </div>
        </div>

        <div class="flex justify-between items-center pb-2">
          <NText variant="caption1" weight="medium">
            {{ t('modules.order.order_value') }}
          </NText>

          <NLoading
            v-if="isLoadingSymbol"
            class="text-brand-primary"
            size="10"
          />

          <div v-else>
            <NumberFormatter
              #="{ number }"
              :value="getSltpOrderValue(order, wage, isSLTPOrderDone)"
              round
            >
              <NText
                :key="number"
                variant="caption1"
                weight="medium"
                class="mx-1"
              >
                {{ number }}
              </NText>
            </NumberFormatter>

            <NText variant="overline" class="text-neutral-secondary">
              {{ t('misc.rial') }}
            </NText>
          </div>
        </div>

        <div class="flex justify-between items-center pb-2">
          <NText variant="caption1" weight="medium">
            {{ t('modules.order.executedValue') }}
          </NText>

          <div class="flex flex-row items-center">
            <NumberFormatter #="{ number }" :value="order.executedValue">
              <NText variant="caption1" weight="medium" class="pe-1">
                {{ number }}
              </NText>
            </NumberFormatter>

            <NText variant="overline" class="text-neutral-secondary">
              {{ t('misc.rial') }}
            </NText>
          </div>
        </div>
      </template>

      <div
        class="flex justify-between items-center py-2 border-t border-neutral-tertiary"
      >
        <NText variant="caption1" weight="medium">
          {{ t('modules.order.creationTime') }}
        </NText>

        <NText variant="caption1" weight="medium">
          {{ creationTime(order.entryDate) }}
        </NText>
      </div>

      <div class="flex justify-between items-center">
        <NText variant="caption1" weight="medium">
          {{ t('orders.valid_until') }}
        </NText>

        <NText variant="caption1" weight="medium">
          {{ orderValidity[order.validityTypeId] }}
        </NText>
      </div>
    </div>

    <NButton
      v-if="from === SLTP_DETAIL.order && isOpenOrder(order)"
      ref="deleteOrderButtonRef"
      variant="outline"
      color="red"
      class="w-full mt-6"
      size="small"
      @click="deleteOrderDialog"
    >
      <NText variant="caption1" weight="medium">
        {{ t('modules.order.cancelOrder') }}
      </NText>
    </NButton>
  </div>
</template>
<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { OrderItem } from '@libs/api-reference';
import { getSymbolIsin, useSymbolDeductions } from '@core/symbol';
import {
  algorithmAverageTransactionPrice,
  getSltpOrderValue,
  isDoneOrder,
  isOpenOrder,
  ORDERS_QUERY_KEYS,
  VALIDITY_TYPES,
} from '@core/order';
import { NText, useDialog, useToast } from '@negar/core';
import { useDeleteOfflineOrderQuery } from '@core/order';
import { sleep } from '@libs/utils';
import { useQueryClient } from '@libs/use';

import NumberFormatter from '@/components/Number.vue';
import ChangePriceEffect from '@/components/ChangePriceEffect.vue';

import {
  Retail,
  SymbolTradeStateIcon,
  useLiveSymbolQuery,
} from '@/modules/symbol';
import { DeleteOrderDialogFooter, SLTP_DETAIL } from '@/modules/order';

type SLTPPortfolioDetailProps = {
  instrumentName: string;
  order: OrderItem;
  from: (typeof SLTP_DETAIL)[keyof typeof SLTP_DETAIL];
};

const props = defineProps<SLTPPortfolioDetailProps>();

const { add: addDialog, removeAll: removeAllDialog } = useDialog();
const toast = useToast();

const queryClient = useQueryClient();

const isin = ref(props.order?.instrumentIdentification as string);

const { findWageConfigBySymbol } = useSymbolDeductions();
const { symbol, isLoadingSymbol } = useLiveSymbolQuery(isin);

const deleteOrderButtonRef = ref<HTMLElement>();

const wage = findWageConfigBySymbol(props.order);

const { t, d } = useI18n();

const creationTime = (date) =>
  `${d(date, 'shortDate')} ,
    ${d(date, 'shortTime')}`;

const { deleteOfflineOrder, isLoadingDeleteOfflineOrder } =
  useDeleteOfflineOrderQuery({
    async onSuccess(result, { closeDeleteOrderDialog }) {
      toast.neutral({
        message: t('modules.order.cancelOrderMessage'),
        duration: 3000,
      });

      await sleep(1000);

      queryClient.invalidateQueries(ORDERS_QUERY_KEYS.orders);

      removeAllDialog();

      closeDeleteOrderDialog();
    },
    onReject(error) {
      if (error) {
        for (const { message } of error.response.data) {
          toast.negative({
            message,
            duration: 3000,
          });
        }
      }
    },
  });

const validityDateTitle = computed(() => {
  const creationDay = new Date(props.order.entryDate).getDate();
  const today = new Date().getDate();

  return creationDay === today
    ? t('misc.today')
    : d(props.order.entryDate, 'shortDate');
});

const orderValidity = computed(() => ({
  [VALIDITY_TYPES.Day]: validityDateTitle.value,
  [VALIDITY_TYPES.GoodTillCancelled]: t('common.cancellation'),
  [VALIDITY_TYPES.GoodTillDate]: props.order.validityDate
    ? d(props.order.validityDate, 'shortDate')
    : '',
}));

const deleteOrderDialog = () => {
  addDialog({
    bodyClass: '!w-1/5',
    title: t('modules.order.cancelOrder'),
    content: t('modules.order.cancelOrderQuestion', {
      instrumentName: props.instrumentName,
      orderSide: t('common.sell'),
    }),
    referenceElementRef: deleteOrderButtonRef,
    placement: 'top',
    footer: computed(() => ({
      component: DeleteOrderDialogFooter,
      props: {
        order: props.order,
        isFromPortfolio: false,
        loadingDeleteOrder: isLoadingDeleteOfflineOrder.value,
        onDeleteOrder: deleteOfflineOrder,
      },
    })),
  });
};

const isSLTPOrderDone = computed(() => {
  if (props.from === SLTP_DETAIL.history && 'newOrderState' in props.order) {
    return isDoneOrder(props.order, Boolean(props.order.newOrderState));
  }

  return isDoneOrder(props.order);
});
</script>
